import { useMemo, forwardRef } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  getChartColors,
  chartDefaults,
  formatCurrency,
  chartContainerStyle,
} from '../utils/chartConfig';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

/**
 * WaterfallChart - Shows progression from savings to strategic value
 *
 * Visualizes how operational savings translate to higher-level business metrics:
 * - Annual Savings (base)
 * - Revenue Equivalence (multiplied by profit margin)
 * - Enterprise Value (multiplied by EBITDA multiple)
 *
 * Uses floating bars technique for waterfall effect.
 * Supports forwardRef for chart image export to PDF.
 *
 * @param {Object} cfoMetrics - CFO metrics object with tier1-6 data
 * @param {React.Ref} ref - Optional ref forwarded to the Bar chart for image export
 */
const WaterfallChart = forwardRef(function WaterfallChart({ cfoMetrics }, ref) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const colors = getChartColors(theme.palette.mode);

  // Build waterfall data
  const chartData = useMemo(() => {
    if (!cfoMetrics?.tier1_ebitda) return null;

    const annualSavings = cfoMetrics?.tier1_ebitda?.contribution || 0;
    const revenueEquiv = cfoMetrics.tier2_revenue?.equivalentRevenue || 0;
    const enterpriseValue =
      cfoMetrics.tier3_valuation?.enterpriseValueIncrease ||
      cfoMetrics.tier3_valuation?.marketCapIncrease ||
      cfoMetrics.tier3_valuation?.valueIncrease ||
      0;

    // Waterfall data structure:
    // Each bar has [bottom, top] values to create floating effect
    // For increasing values, bottom = running total, top = new value added

    const labels = [
      'Annual\nSavings',
      'Revenue\nEquivalent',
      'Enterprise\nValue',
    ];

    // Floating bar data: [start, end] for each bar
    // First bar starts at 0
    // Subsequent bars start where previous ended
    const floatingData = [
      [0, annualSavings], // Annual savings: 0 to value
      [0, revenueEquiv], // Revenue equiv: 0 to value (not stacked)
      [0, enterpriseValue], // Enterprise value: 0 to value
    ];

    return {
      labels,
      datasets: [
        {
          label: 'Value',
          data: floatingData,
          backgroundColor: [
            colors.teal,
            colors.blue,
            colors.primary,
          ],
          borderColor: [
            colors.teal,
            colors.blue,
            colors.primary,
          ],
          borderWidth: 0,
          borderRadius: 6,
          borderSkipped: false,
          barPercentage: 0.6,
        },
      ],
      // Store values for tooltips
      _values: {
        annualSavings,
        revenueEquiv,
        enterpriseValue,
        profitMargin: cfoMetrics.tier2_revenue?.profitMarginUsed || 0.05,
        ebitdaMultiple: cfoMetrics.tier3_valuation?.multipleUsed || 8,
      },
    };
  }, [cfoMetrics]);

  // Chart options
  const options = useMemo(() => {
    return {
      ...chartDefaults,
      indexAxis: 'x',
      plugins: {
        ...chartDefaults.plugins,
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
        },
        tooltip: {
          ...chartDefaults.plugins.tooltip,
          callbacks: {
            title: (items) => {
              return items[0].label.replace('\n', ' ');
            },
            label: (context) => {
              const value = context.parsed.y;
              return `Value: ${formatCurrency(value)}`;
            },
            afterLabel: (context) => {
              if (!chartData?._values) return '';

              const index = context.dataIndex;
              const { profitMargin, ebitdaMultiple, annualSavings } =
                chartData._values;

              switch (index) {
                case 0:
                  return 'Direct annual cost reduction';
                case 1:
                  return `At ${(profitMargin * 100).toFixed(1)}% profit margin`;
                case 2:
                  return `At ${ebitdaMultiple}x EBITDA multiple`;
                default:
                  return '';
              }
            },
          },
        },
      },
      scales: {
        x: {
          ...chartDefaults.scales.x,
          ticks: {
            ...chartDefaults.scales.x.ticks,
            font: {
              size: 11,
              weight: 600,
            },
            maxRotation: 0,
          },
        },
        y: {
          ...chartDefaults.scales.y,
          beginAtZero: true,
          ticks: {
            ...chartDefaults.scales.y.ticks,
            callback: (value) => formatCurrency(value),
          },
        },
      },
    };
  }, [chartData]);

  if (!chartData) {
    return null;
  }

  const { annualSavings, revenueEquiv, enterpriseValue } = chartData._values;

  return (
    <Box sx={{ my: 4 }}>
      {/* Section Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            letterSpacing: '-0.01em',
            mb: 0.5,
          }}
        >
          From Savings to Strategic Value
        </Typography>
        <Typography variant="body2" color="text.secondary">
          How operational efficiency translates to business value
        </Typography>
      </Box>

      {/* Chart Container */}
      <Box sx={{ ...chartContainerStyle, height: 320 }}>
        <Bar ref={ref} data={chartData} options={options} />
      </Box>

      {/* Value Callouts */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mt: 3,
          gap: 2,
        }}
      >
        {/* Annual Savings */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 2,
            background: 'rgba(78, 205, 196, 0.1)',
            border: '1px solid rgba(78, 205, 196, 0.3)',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Annual Savings
          </Typography>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: isDark ? colors.teal : 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {formatCurrency(annualSavings)}
          </Typography>
        </Box>

        {/* Arrow */}
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
          →
        </Box>

        {/* Revenue Equivalent */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 2,
            background: 'rgba(99, 179, 237, 0.1)',
            border: '1px solid rgba(99, 179, 237, 0.3)',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Revenue Equivalent
          </Typography>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: isDark ? colors.blue : 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {formatCurrency(revenueEquiv)}
          </Typography>
        </Box>

        {/* Arrow */}
        <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
          →
        </Box>

        {/* Enterprise Value */}
        <Box
          sx={{
            flex: 1,
            p: 2,
            borderRadius: 2,
            background: 'rgba(160, 206, 78, 0.1)',
            border: '1px solid rgba(160, 206, 78, 0.3)',
            textAlign: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Enterprise Value
          </Typography>
          <Typography
            sx={{
              fontSize: '1.1rem',
              fontWeight: 700,
              color: isDark ? colors.primary : 'text.primary',
              fontFamily: '"JetBrains Mono", monospace',
            }}
          >
            {formatCurrency(enterpriseValue)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
});

export default WaterfallChart;
